import { asIfAccessToken } from '@/features/as-if/asIfView';
import { deviceLocal } from '@/privacy/deviceStorage';
import type { IShellReplyValues, ShellPushStatusType } from '@featit/shared';
import { API_BASE_URL, ApiService, decodeStoredCredentialKind } from '@/services/ApiService';
import { TOKEN_KEY, ownAccessToken } from '@/services/accessToken';
import { historyIndex } from './backNavigation';
import { postShellMessage, requestShell } from './shellBridge';
import { hasShellCapability, shellDescriptor } from './shellDetection';

/**
 * The web's half of push in the app (F-0234, ADR-116 amended): the shell hands over the APNs token,
 * and the web registers it over its own session.
 *
 * ⛔ The shell holds no session of its own for push. It never calls the API; the web registers the
 * device and removes it, so there is never a second credential racing the web's refresh.
 */

/** Where the registered device's id is kept, per deployment, beside that deployment's session. */
export const PUSH_DEVICE_KEY = `${TOKEN_KEY}:pushDevice`;

/**
 * F-0234 (AC-9): "asked after sign-in, the first time the Decisions tab shows ... never at launch."
 *
 * The id of the signed-in person this was last asked for, kept at module scope rather than in a
 * component: the Decisions tab is a route, mounted and unmounted on every navigation to and from
 * it, and a component-local guard would re-ask on the second visit. A different person signing in
 * later (a shared device) is asked again, because `askedFor` changes with them.
 */
let askedFor: string | null = null;

/**
 * Asks at most once per signed-in person and launch, and never for `userId === null` (signed out).
 * Called by `requestShellPushInContext`, never from a screen a person merely lands on.
 */
export function requestShellPushOnceSignedIn(userId: string | null): void {
  if (!userId || askedFor === userId) return;
  askedFor = userId;
  void registerShellPush();
}

/**
 * F-0234 (AC-9, the captain's ruling 2026-10-07): the automatic ask comes in context — the first
 * time the person opens a decision, or once they have answered one, whichever comes first — and
 * never on landing. A restored session lands on Decisions, and a restored or linked decision is a
 * landing too: `opened` counts only a decision reached by navigating in this launch (the router's
 * history index past the first entry). An answer always counts.
 */
export function requestShellPushInContext(
  userId: string | null,
  moment: 'opened' | 'answered',
): void {
  if (moment === 'opened' && historyIndex() === 0) return;
  requestShellPushOnceSignedIn(userId);
}

/**
 * AC-9: what notifications are on this phone, as the system has them, or `null` outside an app
 * that answers it (`pushSettings`) — a browser, or an older app.
 */
export async function shellPushStatus(): Promise<ShellPushStatusType | null> {
  if (!shellDescriptor() || !hasShellCapability('pushSettings')) return null;
  try {
    const answer = await requestShell<IShellReplyValues['pushStatus']>({ type: 'pushStatus' });
    return answer?.status ?? null;
  } catch {
    return null;
  }
}

/** AC-9: the person's own "Turn on" in Settings: the system's prompt, past a kept Not Now. */
export function turnOnShellPush(): Promise<ShellPushOutcomeType> {
  return registerShellPush({ explicit: true });
}

/** AC-9: open this app's Notifications page in iOS Settings, where a denied permission is turned back on. */
export function openShellAppSettings(): void {
  if (hasShellCapability('pushSettings')) postShellMessage({ type: 'openAppSettings' });
}

/** Tests only: forget who this was last asked for. */
export function resetShellPushAskedForTests(): void {
  askedFor = null;
  registeredAtLaunchFor = null;
}

/**
 * F-0534 PPD-3 (I-1238): a phone hears a person only while the session that registered it lives, so a
 * device registered before that rule, or under a session since renewed by a new sign-in, must be
 * registered again. Every launch does it, for the person signed in, once push is authorized: the
 * shell answers a quiet request (`ask: false`) without prompting, and registering is idempotent on
 * the server. Once per web view and person; the tabs' web views of one launch share
 * `LAUNCH_REGISTRATION_KEY`, so one launch registers once.
 */
let registeredAtLaunchFor: string | null = null;

/** When this phone last registered quietly, and for whom: one launch's tabs register once. */
export const LAUNCH_REGISTRATION_KEY = 'featit:pushRegisteredAtLaunch';
const LAUNCH_REGISTRATION_WINDOW_MS = 10 * 60 * 1000;

export function registerShellPushAtLaunch(userId: string | null, now = Date.now()): void {
  if (!userId || registeredAtLaunchFor === userId) return;
  // I-2189 siblings: a verification session is read-only and registers no device; the quiet launch
  // registration is skipped for it (an explicit turn-on still asks, and is refused in words).
  if (decodeStoredCredentialKind() === 'verification') return;
  if (!shellDescriptor() || !hasShellCapability('pushQuietly')) return;
  registeredAtLaunchFor = userId;
  try {
    const last = JSON.parse(deviceLocal.getItem(LAUNCH_REGISTRATION_KEY) ?? 'null') as {
      user?: string;
      at?: number;
    } | null;
    if (
      last?.user === userId &&
      typeof last.at === 'number' &&
      now - last.at < LAUNCH_REGISTRATION_WINDOW_MS
    ) {
      return;
    }
    deviceLocal.setItem(LAUNCH_REGISTRATION_KEY, JSON.stringify({ user: userId, at: now }));
  } catch {
    // Without storage each tab registers; the server takes the same token as one device.
  }
  void registerShellPush({ ask: false });
}

interface IPushDevice {
  id: string;
}

class PushDeviceApi extends ApiService {
  register(body: {
    token: string;
    platform: 'ios';
    apnsEnvironment: 'sandbox' | 'production';
    bundleId: string;
    appVersion: string;
  }): Promise<IPushDevice> {
    return this.put('/me/push-devices', body);
  }
}

const pushDeviceApi = new PushDeviceApi();

export type ShellPushOutcomeType = 'registered' | 'declined' | 'unavailable' | 'failed';

/**
 * Asks the shell for its push token and registers it. Called once a person is signed in, never at
 * launch, so the system's permission prompt follows signing in. Idempotent on the server: the
 * token is unique, and registering it again refreshes the same device.
 */
export async function registerShellPush({
  ask = true,
  explicit = false,
}: { ask?: boolean; explicit?: boolean } = {}): Promise<ShellPushOutcomeType> {
  // F-0709: a device's push registration is the person's own; a tab holding an as-if view has none.
  if (asIfAccessToken() !== null) return 'unavailable';
  const descriptor = shellDescriptor();
  if (!descriptor || !hasShellCapability('push')) return 'unavailable';
  // I-1238: a shell that would prompt for a quiet request is never sent one.
  if (!ask && !hasShellCapability('pushQuietly')) return 'unavailable';
  // An older shell would treat an explicit ask as the automatic one, priming and all.
  if (explicit && !hasShellCapability('pushSettings')) return 'unavailable';
  try {
    /*
     * I-1070: the answer is the person's — the app's reason, then the system prompt, possibly on a
     * later screen when the one that asked could not show it — and routinely takes longer than the
     * bridge's 10 s. Cut off there, the token that arrived later was thrown away and the device was
     * never registered. Nothing on screen waits on this.
     */
    const answer = await requestShell<IShellReplyValues['pushToken']>(
      explicit
        ? { type: 'pushToken', explicit: true }
        : ask
          ? { type: 'pushToken' }
          : { type: 'pushToken', ask: false },
      { timeoutMs: null },
    );
    if (!answer) return 'declined';
    if (typeof answer.token !== 'string' || typeof answer.bundleId !== 'string') {
      console.warn(
        '[featit] push: the app answered without a token or a bundle id; not registered',
      );
      return 'failed';
    }
    const device = await pushDeviceApi.register({
      token: answer.token,
      platform: 'ios',
      apnsEnvironment: answer.environment,
      bundleId: answer.bundleId,
      appVersion: descriptor.version,
    });
    try {
      deviceLocal.setItem(PUSH_DEVICE_KEY, device.id);
    } catch {
      /* Without storage the device is still registered; sign-out just cannot name it. */
    }
    return 'registered';
  } catch (error) {
    console.warn('[featit] push: registration failed', error);
    return 'failed';
  }
}

/**
 * Removes this phone's registration, on sign-out. Synchronous up to the request: it reads the
 * device id and the session **before** the caller clears them, then sends the removal with that
 * session. The request is fire-and-forget (`keepalive`), because a sign-out must not wait on it.
 */
export function forgetShellPushDevice(): void {
  let deviceId: string | null = null;
  let accessToken: string | null = null;
  try {
    deviceId = deviceLocal.getItem(PUSH_DEVICE_KEY);
    accessToken = ownAccessToken();
    deviceLocal.removeItem(PUSH_DEVICE_KEY);
  } catch {
    return;
  }
  if (!deviceId || !accessToken) return;
  void fetch(`${API_BASE_URL}/me/push-devices/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
    keepalive: true,
  }).catch((error: unknown) => console.warn('[featit] push: removal failed', error));
}
